import { CSSProperties, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../../../../services/api'
import TableHeader from './Header'
import { LinkContainer } from './style'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { useLoading } from '../../../../../hooks/loading'
import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { genericMaskWithTwoZeroWithPoint } from '../../../../../utlis/mask'
import currency from 'currency.js'

interface Action {
  name: string
  icon?: IconDefinition
  spanIcon?: string
  title: string
  link?: string
  classname?: string
  activeClassname?: string
  style?: CSSProperties
  onClick?: (params: any) => void
  linkTo?: (params: any) => string
}

interface Header {
  name: string
  field: string
  sortable: boolean
  download?: {
    fileName?: string
    fileUrl?: string
  }
  hasTypes?: {
    types?: any
  }
  concat?: {
    field?: string
    position?: 'start' | 'end'
  }
}

interface SearchParameters {
  [key: string]: string
}

interface DataTableProps {
  onActions?: {
    onClickButtonEdit?: <T>(currentValue: T | any) => void
    onClickButtonRemove?: <T>(currentValue: T | any) => void
    onClickButtonList?: <T>(currentValue: T | any) => void
  }
  entity: string
  source: string
  headers?: Header[]
  actions?: Action[]
  notHasChildren?: boolean
  onlyParent?: boolean
  notParent?: boolean
  onlyView?: boolean
  parentId?: string
  viewField?: { source: string; field: string }
  entityId?: string
  searchParameters?: SearchParameters[]
  showParentId?: boolean
  format: {
    orderBy: string
  }
  excludeColumn?: string
  excludeKey?: string
  activeItems?: number[]
}

const DataTable = ({
  onActions,
  entity,
  source,
  notHasChildren,
  viewField,
  onlyView,
  onlyParent,
  notParent,
  excludeColumn,
  excludeKey,
  headers = [
    { name: 'Data', field: 'created_at', sortable: true },
    { name: 'Descrição', field: 'descriptions', sortable: true },
    { name: 'Ação', field: 'type', sortable: true }
  ],
  actions,
  format,
  parentId,
  entityId,
  searchParameters,
  showParentId,
  activeItems
}: DataTableProps): JSX.Element => {
  const [items, setItems] = useState<any[]>([])
  const { isUpdated } = useUpdateDataTable()

  const { activeLoading, disableLoading } = useLoading()

  const handlerOnClickButtonList = (currentValue: any) => {
    if (viewField) {
      return `/${viewField.source}/view/${currentValue[viewField.field]}?tab=${
        currentValue.id
      }`
    }

    if (typeof onActions?.onClickButtonList === 'function') {
      onActions.onClickButtonList(currentValue)
    } else {
      return `/${source}/view/${currentValue.id}`
    }
  }

  const handlerOnClickButtonEdit = (currentValue: any) => {
    return `/${source}/update/${currentValue.id}`
  }

  const handlerOnClickButtonRemove = (currentValue: any) => {
    if (onActions?.onClickButtonRemove) {
      onActions.onClickButtonRemove(currentValue)
    }
  }

  const loadParams = useCallback(() => {
    const params = {
      entity,
      source,
      keyword: '',
      orderByField: '',
      searchParameters,
      onlyParent,
      notParent,
      orderBy: format.orderBy,
      parentId,
      entityId,
      excludeColumn,
      excludeKey,
      page: 0,
      perPage: 0
    }

    if (!parentId) Object.assign(params, { parentId: '' })
    if (!entityId) Object.assign(params, { entityId: '' })

    return params
  }, [
    entity,
    entityId,
    excludeColumn,
    excludeKey,
    format.orderBy,
    onlyParent,
    notParent,
    parentId,
    searchParameters,
    source
  ])

  useEffect(() => {
    ;(async () => {
      try {
        activeLoading()
        const params = loadParams()
        const response = await api.get('dataTable', { params })

        setItems(response.data.items)
        disableLoading()
      } catch (error) {
        disableLoading()
        console.error(error)
      }
    })()
  }, [loadParams, isUpdated, activeLoading, disableLoading])

  const handleDownload = useCallback((item: any, header: Header) => {
    let filename = item[header.download.fileName]
    const indexTrace = filename.indexOf('-') + 1
    filename = filename.slice(indexTrace, filename.length)
    fetch(`${item[header.download.fileUrl]}`).then(response => {
      response.blob().then((blobObject: any) => {
        console.log(blobObject)
        const url = window.URL.createObjectURL(blobObject)
        const a = document.createElement('a')
        a.download = filename
        a.href = url
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      })
    })
  }, [])

  return (
    <div>
      <div className="card-header p-0 d-flex flex-column py-5 gap-2 gap-md-5 border-gray-300">
        <div className="card-title m-0 fw-bolder fs-3 text-gray-800 mt-5">
          Movimentações
        </div>
      </div>
      <div className="table-scrollable">
        <table className="table table-row-bordered dataTable no-footer table gs-3 table-row-gray-300">
          <TableHeader
            headers={headers}
            onSorting={(field, order) => {
              const itemSorted = items.sort((a, b) => {
                const fields = field.split('.')
                let currentFieldA
                let currentFieldB
                if (fields.length === 1) {
                  currentFieldA = a[fields[0]]
                  currentFieldB = b[fields[0]]
                }
                if (fields.length === 2) {
                  currentFieldA = a[fields[0]]?.[fields[1]]
                  currentFieldB = b[fields[0]]?.[fields[1]]
                }
                if (fields.length === 3) {
                  currentFieldA = a[fields[0]]?.[fields[1]]?.[fields[2]]
                  currentFieldB = b[fields[0]]?.[fields[1]]?.[fields[2]]
                }
                if (fields.length === 4) {
                  currentFieldA =
                    a[fields[0]]?.[fields[1]]?.[fields[2]]?.[fields[3]]
                  currentFieldB =
                    b[fields[0]]?.[fields[1]]?.[fields[2]]?.[fields[3]]
                }
                return currentFieldA > currentFieldB && order === 'ASC' ? 1 : -1
              })
              setItems([...itemSorted])
            }}
          />
          <tbody>
            {(items.length > 0 &&
              items.map(item => {
                const types = [
                  { key: 'locacao', label: 'LOCAÇÃO' },
                  { key: 'L', label: 'LOCAÇÃO' },
                  { key: 'materia-prima', label: 'MATERIA PRIMA' },
                  { key: 'revenda', label: 'REVENDA' },
                  { key: 'semi-acabado', label: 'SEMI ACABADO' },
                  { key: 'consumo', label: 'USO E CONSUMO' },
                  { key: 'CONSUMO', label: 'USO E CONSUMO' },
                  { key: 'venda', label: 'VENDA' },
                  { key: 'V', label: 'VENDA' },
                  { key: 'C', label: 'Crédito' },
                  { key: 'D', label: 'Débito' }
                ]
                const typeProduct = types.find(
                  t => t.key === item.product?.type
                )
                if (typeProduct) {
                  item.product.type = typeProduct.label
                }
                const type = types.find(t => t.key === item.type)
                if (type) {
                  item.type = type.label
                }

                return (
                  <tr key={item.id}>
                    {headers.map(
                      header =>
                        (header?.field !== 'actions' &&
                          !header?.download &&
                          !header?.hasTypes &&
                          header.field !== 'checkbox' &&
                          header.name !== 'Saldo' && (
                            <td key={`${header?.field}-${item.id}`}>
                              <p
                                style={{
                                  textAlign: 'left'
                                }}
                              >
                                {typeof item[header?.field] === 'boolean' &&
                                  (item[header?.field] ? 'Sim' : 'Não')}
                                {header?.field.split('.').length === 4 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]?.[header?.field.split('.')[2]]?.[
                                    header?.field.split('.')[3]
                                  ]}
                                {header?.field.split('.').length === 3 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]?.[header?.field.split('.')[2]]}
                                {header?.field.split('.').length === 2 &&
                                  item[header?.field.split('.')[0]]?.[
                                    header?.field.split('.')[1]
                                  ]}
                                {header?.concat
                                  ? header?.concat?.position === 'start'
                                    ? `${item[header?.concat?.field]} ${
                                        item[header?.field]
                                      }`
                                    : `${item[header?.field]} ${
                                        item[header?.concat?.field]
                                      }`
                                  : header?.field.split('.').length === 1 &&
                                    item[header?.field]}
                              </p>
                            </td>
                          )) ||
                        (header?.download && (
                          <td key={`${header?.field}-${item.id}`}>
                            <a
                              href="#"
                              onClick={() => handleDownload(item, header)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start'
                              }}
                            >
                              <span
                                className="fa fa-download"
                                style={{ marginRight: '5px' }}
                              ></span>
                              <span>Download</span>
                            </a>
                          </td>
                        )) ||
                        (header?.name === 'Saldo' && (
                          <td key={`${header?.field}-${item.id}`}>
                            <p
                              style={{
                                textAlign: 'left'
                              }}
                            >
                              {currency(item[header?.field], {
                                symbol: '',
                                decimal: ',',
                                separator: '.'
                              }).format()}{' '}
                              C
                            </p>
                          </td>
                        )) ||
                        (header?.hasTypes && (
                          <td key={`${header?.field}-${item.id}`}>
                            <p style={{ textAlign: 'left' }}>
                              {header.hasTypes.types.monetary &&
                                !header.concat &&
                                genericMaskWithTwoZeroWithPoint(
                                  item[header?.field]
                                )}
                              {header.hasTypes.types.monetary &&
                                header.concat &&
                                `${genericMaskWithTwoZeroWithPoint(
                                  item[header?.field]
                                )} ${item[header.concat.field]}`}
                              {header.hasTypes.types.date &&
                                item[header?.field].slice(0, 10)}
                              {!header.hasTypes?.types &&
                                header.hasTypes.types[item[header?.field]]}
                            </p>
                          </td>
                        )) || (
                          <td
                            key={`actions-${item.id}`}
                            className="actions border"
                          >
                            {(actions && (
                              <LinkContainer>
                                {actions.map(action => (
                                  <Link
                                    className={
                                      activeItems?.length &&
                                      activeItems.includes(item.id)
                                        ? `${action.classname} ${action.activeClassname}`
                                        : `${action.classname}`
                                    }
                                    to={
                                      action.link ||
                                      (action.linkTo && action.linkTo(item)) ||
                                      '#'
                                    }
                                    key={action.name}
                                    title={action.title}
                                    onClick={e => {
                                      if (action.onClick) {
                                        e.preventDefault()
                                        action.onClick(item)
                                      }
                                    }}
                                  >
                                    {action.spanIcon ? (
                                      <span
                                        className={action.spanIcon}
                                        style={action.style}
                                      />
                                    ) : (
                                      <FontAwesomeIcon icon={action.icon} />
                                    )}
                                  </Link>
                                ))}
                              </LinkContainer>
                            )) || (
                              <LinkContainer>
                                {showParentId && (
                                  <Link
                                    className="link"
                                    key={Math.random()}
                                    title="Visualizar"
                                    to={() => handlerOnClickButtonList(item)}
                                  >
                                    <span className="fa fa-search" />
                                  </Link>
                                )}
                                {!showParentId && !item.parent_id && (
                                  <Link
                                    className="link"
                                    key={Math.random()}
                                    title="Visualizar"
                                    to={() => handlerOnClickButtonList(item)}
                                  >
                                    <span className="fa fa-search" />
                                  </Link>
                                )}
                                <div>
                                  {!onlyView && (
                                    <Link
                                      className="link"
                                      key={Math.random()}
                                      title="Editar"
                                      onClick={e => {
                                        if (onActions?.onClickButtonEdit) {
                                          e.preventDefault()
                                          onActions.onClickButtonEdit(item)
                                        }
                                      }}
                                      to={() => handlerOnClickButtonEdit(item)}
                                    >
                                      <span className="fa fa-edit" />
                                    </Link>
                                  )}
                                  {!notHasChildren && (
                                    <a
                                      href="#"
                                      key={Math.random()}
                                      title="Remover"
                                      className="link"
                                      onClick={() => {
                                        handlerOnClickButtonRemove(item)
                                      }}
                                    >
                                      <span className="fa fa-remove" />
                                    </a>
                                  )}
                                </div>
                              </LinkContainer>
                            )}
                          </td>
                        )
                    )}
                  </tr>
                )
              })) || (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: 'center' }}>
                  Nenhum registro encontrado
                </td>
              </tr>
            )}
          </tbody>
          <tfoot />
        </table>
      </div>
    </div>
  )
}

export default DataTable
